import { Suspense, useMemo, useRef, useCallback, useState, type Ref } from 'react';
import { NavigationControlsEventListener } from '@/components';
import { Canvas, useLoader, useFrame, useThree, createPortal } from '@react-three/fiber';
import { CameraControls, Line, PerspectiveCamera, TransformControls } from '@react-three/drei';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { NoToneMapping, MathUtils, Mesh, MeshBasicMaterial, Vector3, Group, Scene, PerspectiveCamera as ThreePerspectiveCamera } from 'three';
import { SubjectDraft } from '@/contexts';
import { Loader } from '@/components';
import { useContextCameraTracking } from '../hooks/useContextCameraTracking';
import { useTransformDrag } from '../hooks/useTransformDrag';

// ─── Circle (matches main viewer) ────────────────────────────────────────────

const drawCircle = (radius: number) => {
  const points = [];
  for (let i = 0; i <= 360; i++) {
    points.push(
      new Vector3(
        Math.sin(MathUtils.degToRad(i)) * radius,
        Math.cos(MathUtils.degToRad(i)) * radius,
        0
      )
    );
  }
  return points;
};

const Circle = ({ radius, rotation }: { radius: number; rotation?: [number, number, number] }) => (
  <Line
    points={drawCircle(radius)}
    color={0xffffff}
    lineWidth={0.75}
    opacity={0.1}
    transparent
    rotation={rotation ?? [0, 0, 0]}
  />
);

// ─── Model loaders ────────────────────────────────────────────────────────────

interface ContextModelProps {
  blobUrl: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  wireframeColor: number;
  opacity: number;
  ref?: Ref<Group>;
}

const AdminContextModel = ({ blobUrl, position, rotation, scale, wireframeColor, opacity, ref }: ContextModelProps) => {
  const model = useLoader(FBXLoader, blobUrl);

  model.traverse((child) => {
    if (child.type === 'Mesh') {
      (child as Mesh).material = new MeshBasicMaterial({
        wireframe: true,
        color: wireframeColor,
        transparent: true,
        opacity,
      });
    }
  });

  return (
    <group
      ref={ref}
      position={position}
      rotation={[MathUtils.degToRad(rotation[0]), MathUtils.degToRad(rotation[1]), MathUtils.degToRad(rotation[2])]}
      scale={scale}
    >
      <primitive object={model} />
    </group>
  );
};

// ─── Camera-synced group (matches main viewer Context.tsx behaviour) ──────────

type Transform3D = {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
};

interface AdminContextSceneProps {
  draft: SubjectDraft;
  transformMode?: 'translate' | 'rotate' | 'scale' | null;
  transformTarget?: 'reference' | 'subject' | 'fullContext' | null;
  onContextTransformChange?: (target: 'reference' | 'subject' | 'fullContext', t: Transform3D) => void;
}

export const AdminContextScene = ({ draft, transformMode, transformTarget, onContextTransformChange }: AdminContextSceneProps) => {
  const { config3D } = draft;
  const { context } = config3D;
  const transformControlsRef = useRef<any>(null);
  const { controls } = useThree() as { controls: CameraControls | null };
  const controlsRef = useRef<CameraControls | null>(null);
  controlsRef.current = controls;

  // State-backed callback refs so React re-renders when models mount, making each
  // group reactively available as the TC object prop.
  const [refContextGroup, setRefContextGroup] = useState<Group | null>(null);
  const [subjectContextGroup, setSubjectContextGroup] = useState<Group | null>(null);
  const refContextCallback = useCallback((g: Group | null) => setRefContextGroup(g), []);
  const subjectContextCallback = useCallback((g: Group | null) => setSubjectContextGroup(g), []);

  const { groupRef, proxyRef, initialX, initialY, initialZ } = useContextCameraTracking(
    context.fullContext.rotation,
    transformMode,
    transformTarget
  );

  const modelActiveGroup =
    transformTarget === 'reference' ? refContextGroup :
    transformTarget === 'subject' ? subjectContextGroup :
    null;
  const activeGroup = transformTarget === 'fullContext' ? proxyRef.current : modelActiveGroup;

  useTransformDrag(
    transformControlsRef,
    controlsRef,
    activeGroup,
    transformTarget ? (t) => onContextTransformChange?.(transformTarget, t) : undefined,
    `${transformMode}_${transformTarget}`
  );

  // All TCs rendered outside groupRef so gizmo axes are world-aligned (not tilted
  // by the camera-tracking rotation). TC still controls objects inside the group
  // correctly via their world matrices.
  return (
    <>
      <group ref={groupRef} rotation={[initialX, initialY, initialZ]}>
        <Circle radius={3} />
        <Circle radius={3} rotation={[0, Math.PI / 2, 0]} />
        <Circle radius={3} rotation={[Math.PI / 2, 0, 0]} />

        {/* Reference context: green wireframe, low opacity */}
        {config3D._referenceContextBlobUrl && (
          <AdminContextModel
            ref={refContextCallback}
            blobUrl={config3D._referenceContextBlobUrl}
            position={context.referenceContext.position}
            rotation={context.referenceContext.rotation}
            scale={context.referenceContext.scale}
            wireframeColor={0xc3d82e}
            opacity={0.1}
          />
        )}

        {/* Subject context: white wireframe, fully opaque */}
        {config3D._contextBlobUrl && context.subjectContextEnabled && (
          <AdminContextModel
            ref={subjectContextCallback}
            blobUrl={config3D._contextBlobUrl}
            position={context.subjectContext.position}
            rotation={context.subjectContext.rotation}
            scale={context.subjectContext.scale}
            wireframeColor={0xffffff}
            opacity={1}
          />
        )}
      </group>

      {/* Invisible proxy — TC target for fullContext; holds clean base rotation */}
      <group ref={proxyRef} />

      {transformMode && modelActiveGroup && (
        <TransformControls ref={transformControlsRef} object={modelActiveGroup} mode={transformMode} />
      )}
      {transformMode && transformTarget === 'fullContext' && proxyRef.current && (
        <TransformControls ref={transformControlsRef} object={proxyRef.current} mode={transformMode} />
      )}
    </>
  );
};

// ─── Portal (inset bottom-right within a shared Canvas) ──────────────────────

const PORTAL_SIZE = 220;

export const AdminContextPortal = ({ draft }: { draft: SubjectDraft }) => {
  const { config3D } = draft;
  const { context } = config3D;
  const store = useThree();
  const contextScene = useMemo(() => new Scene(), []);
  const contextCamera = useRef<ThreePerspectiveCamera | null>(null);
  const contextGroupRef = useRef<Group | null>(null);

  const initialX = MathUtils.degToRad(context.fullContext.rotation[0]);
  const initialY = MathUtils.degToRad(context.fullContext.rotation[1]);
  const initialZ = MathUtils.degToRad(context.fullContext.rotation[2]);

  useFrame(() => {
    const { gl, scene, camera, size, controls } = store;
    const cameraControls = controls as CameraControls | null;

    if (contextCamera.current) {
      // Render main scene at full viewport
      gl.clear();
      gl.setViewport(0, 0, size.width, size.height);
      gl.setScissor(0, 0, size.width, size.height);
      gl.setScissorTest(true);
      gl.render(scene, camera);

      // Render context portal as inset in bottom-right (WebGL y=0 is bottom)
      contextCamera.current.aspect = 1;
      contextCamera.current.updateProjectionMatrix();
      gl.clearDepth();
      gl.setViewport(size.width - PORTAL_SIZE, 0, PORTAL_SIZE, PORTAL_SIZE);
      gl.setScissor(size.width - PORTAL_SIZE, 0, PORTAL_SIZE, PORTAL_SIZE);
      gl.setScissorTest(true);
      gl.render(contextScene, contextCamera.current);
    }

    if (contextGroupRef.current && cameraControls) {
      contextGroupRef.current.rotation.z = initialZ + cameraControls.azimuthAngle;
      contextGroupRef.current.rotation.x = initialX + Math.PI - cameraControls.polarAngle;
    }
  }, 1);

  return createPortal(
    <>
      <PerspectiveCamera
        ref={contextCamera}
        fov={30}
        aspect={1}
        near={0.1}
        far={40}
        makeDefault={false}
        position={[0, 0, 15]}
      />
      <ambientLight color={0xffffff} intensity={1} />
      <Suspense>
        <group ref={contextGroupRef} rotation={[initialX, initialY, initialZ]}>
          <Circle radius={3} />
          <Circle radius={3} rotation={[0, Math.PI / 2, 0]} />
          <Circle radius={3} rotation={[Math.PI / 2, 0, 0]} />
          {config3D._referenceContextBlobUrl && (
            <AdminContextModel
              blobUrl={config3D._referenceContextBlobUrl}
              position={context.referenceContext.position}
              rotation={context.referenceContext.rotation}
              scale={context.referenceContext.scale}
              wireframeColor={0xc3d82e}
              opacity={0.1}
            />
          )}
          {config3D._contextBlobUrl && context.subjectContextEnabled && (
            <AdminContextModel
              blobUrl={config3D._contextBlobUrl}
              position={context.subjectContext.position}
              rotation={context.subjectContext.rotation}
              scale={context.subjectContext.scale}
              wireframeColor={0xffffff}
              opacity={1}
            />
          )}
        </group>
      </Suspense>
    </>,
    contextScene
  );
};

// ─── Preview ─────────────────────────────────────────────────────────────────

interface AdminContextPreviewProps {
  draft: SubjectDraft;
  transformMode?: 'translate' | 'rotate' | 'scale' | null;
  transformTarget?: 'reference' | 'subject' | 'fullContext' | null;
  onContextTransformChange?: (target: 'reference' | 'subject' | 'fullContext', t: Transform3D) => void;
}

export const AdminContextPreview = ({ draft, transformMode, transformTarget, onContextTransformChange }: AdminContextPreviewProps) => {
  const { config3D } = draft;
  const hasAnyModel = !!config3D._referenceContextBlobUrl || !!config3D._contextBlobUrl;
  const cameraControlsRef = useRef<CameraControls | null>(null);

  const cameraControlsCallback = useCallback((controls: CameraControls | null) => {
    cameraControlsRef.current = controls;
  }, []);

  const resetCamera = () => cameraControlsRef.current?.reset(true);

  return (
    <div
      className="w-full h-full rounded border border-[#333] overflow-hidden relative"
      style={{ background: 'radial-gradient(transparent 20%, rgb(8, 8, 8)), rgb(88, 88, 88)' }}
      onDoubleClick={resetCamera}
    >
      {hasAnyModel && (
        <button
          onClick={(e) => { e.stopPropagation(); resetCamera(); }}
          onDoubleClick={(e) => e.stopPropagation()}
          className="absolute top-2 right-2 z-10 px-2 py-1 rounded text-xs bg-black/50 text-[#CCC] hover:bg-black/70 hover:text-white transition-colors select-none"
          title="Reset view (or double-click canvas)"
        >
          ↺ Reset
        </button>
      )}
      {transformMode && (
        <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded text-xs bg-black/70 text-[#C3D82E] pointer-events-none select-none">
          {transformMode === 'translate' ? '↔ Move' : transformMode === 'rotate' ? '↻ Rotate' : '⇲ Scale'}
          {transformTarget ? ` (${transformTarget === 'reference' ? 'Ref' : transformTarget === 'subject' ? 'Subject' : 'Full Ctx'})` : ''} · orbit paused
        </div>
      )}
      {!hasAnyModel ? (
        <div className="flex items-center justify-center h-full text-[#666] text-sm">
          Upload a context model to preview
        </div>
      ) : (
        <Canvas
          gl={{ precision: 'highp', toneMapping: NoToneMapping }}
          dpr={window.devicePixelRatio}
          camera={{ position: [0, 0, 15], fov: 30, near: 0.1, far: 10000 }}
        >
          <ambientLight color={0xffffff} intensity={1} />
          <CameraControls makeDefault ref={cameraControlsCallback} draggingSmoothTime={0.3} />
          <NavigationControlsEventListener resetViewHandler={resetCamera} cameraControlsRef={cameraControlsRef} />
          <Suspense fallback={<Loader />}>
            <AdminContextScene
              draft={draft}
              transformMode={transformMode}
              transformTarget={transformTarget}
              onContextTransformChange={onContextTransformChange}
            />
          </Suspense>
        </Canvas>
      )}
    </div>
  );
};
